import type { ComponentType } from "react"
import type { CogoElement } from "./types"

/**
 * The DOM attributes that address a rendered Cogo element from outside the
 * runtime (`data-cogo-element-id`, `data-cogo-type`, the edit-mode
 * `data-element-id`, and the runtime block-boundary pair).
 *
 * These attributes are not decoration: publication visual proofs, the Studio
 * selection layer, and the headless conformance harnesses all resolve an
 * element by `[data-cogo-element-id]` and then MEASURE the resolved node with
 * `getBoundingClientRect()`. The node that carries them must therefore be a
 * node that generates a box.
 */
export type CogoElementIdentity = Readonly<Record<string, string>>

export interface RendererProps {
  element: CogoElement
  children?: React.ReactNode
  /**
   * Identity attributes to spread onto the DOM node that generates this
   * element's box. Only supplied to renderers that declare
   * {@link ElementRenderer.cogoOwnsElementIdentity}; for every other renderer
   * `ElementRenderer` emits them on a layout-neutral `display: contents`
   * wrapper instead (see the contract note on `cogoOwnsElementIdentity`).
   */
  identity?: CogoElementIdentity
}

export type ElementRenderer = ComponentType<RendererProps> & {
  /**
   * Declares that this renderer spreads `props.identity` onto the DOM node
   * that actually generates the element's box.
   *
   * Renderers that do NOT declare it keep the historical behaviour:
   * `ElementRenderer` wraps their output in `<div class="contents">` carrying
   * the identity attributes. That wrapper is layout-neutral — `display:
   * contents` removes it from the box tree so children participate directly in
   * the parent's flex/grid context — but for the same reason it generates NO
   * box, so `getBoundingClientRect()` on it returns 0×0 and every consumer that
   * measures the addressed node (publication visual proof, Studio selection
   * overlay, `elementFromPoint`) is left with a degenerate rect.
   *
   * Declaring the flag moves the identity onto the real box AND lets
   * `ElementRenderer` drop the wrapper entirely. Dropping a `display: contents`
   * node is layout-identical by construction: such a node contributes nothing
   * to layout, so removing it cannot change the box tree.
   *
   * Contract for a renderer that declares this flag:
   *  1. Spread `props.identity` on EVERY return path, onto the outermost DOM
   *     node it renders (the node whose box represents the element).
   *  2. Never render more than one node carrying the identity — consumers
   *     assert `document.querySelectorAll('[data-cogo-element-id="…"]')` has
   *     exactly one match.
   */
  cogoOwnsElementIdentity?: true

  /**
   * Declares that this renderer fully owns interpreting its own element's
   * `iterateOver`/`itemVar` — the SAME category `ElementRenderer` already
   * carves out by name for `block-ref` and `carousel` (both resolve their
   * collection and build their own repeated children internally, e.g.
   * `CarouselRenderer` maps `iterateOver` into N `<CarouselItem>`s). A
   * renderer declares this instead of relying on the name check when its own
   * DOM skeleton (e.g. a table's `<table>`/`<thead>`/`<tbody>` boundary)
   * exists exactly once no matter how many items are bound, AND the
   * per-item unit has HTML content-model constraints the generic per-item
   * wrapper can't satisfy (a `<tr>`/`<td>` cannot be wrapped in an
   * intermediate `<div>`, which is how the generic `IteratedElement`/
   * `IteratedContainer` path identifies an item when the renderer hasn't
   * separately opted into {@link cogoOwnsElementIdentity}).
   *
   * `ElementRenderer` SKIPS its generic `iterateOver` handling entirely for
   * such an element (same as it already does for `block-ref`/`carousel`) —
   * the renderer receives `element` with `iterateOver`/`itemVar` intact and
   * is responsible for resolving the collection (`resolveSymbol`) and
   * building its own per-item output, typically via
   * {@link buildIteratedResolveSymbol} for per-item `#{itemVar.x}`
   * expression resolution parity with the generic path.
   *
   * This is a type-level predicate (opt in per renderer via the registry),
   * never a list of element-type names — any current or future renderer with
   * the same shape can declare it.
   */
  cogoIteratesOwnChildren?: true
}

/**
 * True when `renderer` spreads its own identity attributes onto its real box,
 * so `ElementRenderer` must NOT emit them on a wrapper (which would both hide
 * the measurable node behind a boxless one and duplicate the identity).
 */
export function rendererOwnsElementIdentity(
  renderer: ElementRenderer | null | undefined,
): boolean {
  return renderer?.cogoOwnsElementIdentity === true
}

/**
 * True when `renderer` fully owns interpreting `iterateOver`/`itemVar` on its
 * own element — `ElementRenderer` must not intercept them generically. See
 * {@link ElementRenderer.cogoIteratesOwnChildren}.
 */
export function rendererIteratesOwnChildren(
  renderer: ElementRenderer | null | undefined,
): boolean {
  return renderer?.cogoIteratesOwnChildren === true
}

export class ElementRegistry {
  private renderers = new Map<string, ElementRenderer>()

  register(type: string, renderer: ElementRenderer): void {
    this.renderers.set(type, renderer)
  }

  get(type: string): ElementRenderer | null {
    return this.renderers.get(type) ?? null
  }

  types(): string[] {
    return Array.from(this.renderers.keys())
  }
}

// Singleton instance
export const globalRegistry = new ElementRegistry()
